// The bridging comparison engine: the quick estimate, the three refine
// sections, live results and the adviser band. Ported from the
// bridgingeye.co.uk homepage script; the "Get exact terms" dialog and the
// thank-you step are in apply.ts.
import { Parts, scrollToEl } from '../dom';
import { applyMoneyInputFormatting, classifyUkPostcode, escapeHtml, formatMoneyInput, formatPct, labelFor, money, postcodeArea } from '../format';
import type { EngineConfig, MountOptions, ResultRow } from '../types';
import { setupApply, type ApplyAdapter } from '../apply';
import { track, trackOnce, trackWhenSeen } from '../funnel';
import { contingencyGbp, firstChargeCommentBlock, gdvError, refurbCommentBlock, refurbTotalGbp } from './comments';
import { RESULTS_PREVIEW_COUNT, lowestTotalCostGbp, resultRowHtml, resultsHeaderHtml, sortResults, type SortBy } from './results';

const CHECK_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>';
const BADGE_CHECK_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>';

export interface BridgingState {
  purpose: string; propertyType: string; liveIn: string; propertySubtype: string;
  value: number; loan: number; term: string; charge: string; firstChargeGbp: number;
  exit: string; adverse: string; region: string; regionGroup: string; postcode: string;
  applicantType: string; address: string; comments: string;
  quoteId: string | null; results: ResultRow[]; matchedCount: number | null; apiFetchToken: number; sortBy: SortBy;
  selectedCode: string | null; who: string; introMode: 'login' | 'register' | null; introCompanyAddress: string;
  purchaseCostsPct: number; purchaseCostsGbp: number; alreadyOwned: boolean;
  buildCosts: number; professionalCosts: number; contingencyPct: number; gdv: number;
  /** Heavy refurbishment: 'yes' / 'no', or '' until answered. */
  similarProject: string;
  /** Purchase or auction below the property's value, and its price. */
  belowValue: boolean; purchasePrice: number;
}

type NumericStateKey = 'value' | 'loan' | 'firstChargeGbp' | 'purchaseCostsGbp' | 'buildCosts' | 'professionalCosts' | 'gdv' | 'purchasePrice';
type ChoiceKey = 'purpose' | 'propertyType' | 'liveIn' | 'charge' | 'adverse' | 'applicantType' | 'exit' | 'similarProject';

export function startBridgingEngine(parts: Parts, config: EngineConfig, options: MountOptions): void {
  const { form, brand } = config;
  const d = form.defaults;
  const $ = <T extends HTMLElement = HTMLElement>(id: string) => parts.el<T>(id);
  const scrollTo = (el: Element, block: 'start' | 'nearest' | 'center' = 'start') => scrollToEl(el, options.stickyTop, block);

  const state: BridgingState = {
    purpose: d.purpose, propertyType: d.propertyType, liveIn: d.liveIn, propertySubtype: '',
    value: d.value, loan: d.loan, term: String(d.term), charge: d.charge, firstChargeGbp: 0,
    exit: d.exitStrategy, adverse: d.adverse, region: 'england_wales', regionGroup: d.regionGroup, postcode: '',
    applicantType: d.applicantType, address: '', comments: '',
    quoteId: null, results: [], matchedCount: null, apiFetchToken: 0, sortBy: 'totalCost',
    selectedCode: null, who: 'borrower', introMode: null, introCompanyAddress: '',
    purchaseCostsPct: 5, purchaseCostsGbp: 0, alreadyOwned: false,
    buildCosts: 0, professionalCosts: 0, contingencyPct: 5, gdv: 0,
    similarProject: '', belowValue: false, purchasePrice: 0,
  };

  // A landing page's preset answers (e.g. { charge: 'second_charge' }):
  // only known keys with valid values are taken.
  const choices: Record<ChoiceKey, [string, string][]> = {
    purpose: form.purposes, propertyType: form.propertyTypes, liveIn: form.liveIn, charge: form.charges,
    adverse: form.adverse, applicantType: form.applicantTypes, exit: form.exits, similarProject: form.similarProject,
  };
  const preset = options.preset;
  if (preset) {
    (Object.keys(choices) as ChoiceKey[]).forEach((key) => {
      if (choices[key].some((o) => o[0] === preset[key])) state[key] = preset[key] as string;
    });
    const subtypes = form.propertySubtypesByType[state.propertyType] || [];
    if (typeof preset.propertySubtype === 'string' && subtypes.includes(preset.propertySubtype)) state.propertySubtype = preset.propertySubtype;
    (['value', 'loan'] as const).forEach((key) => {
      const v = preset[key];
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) state[key] = v;
    });
    if (typeof preset.term === 'number' && preset.term >= form.term.min && preset.term <= form.term.max) state.term = String(preset.term);
  }

  const isRefurb = () => state.purpose === 'refurb_light' || state.purpose === 'refurb_heavy';
  const isPurchase = () => state.purpose === 'purchase' || state.purpose === 'auction';

  // ---------- Refine: three sections of pill questions ----------
  // A section is complete (tick) once each of its questions has been
  // answered, or its mobile "Continue" pressed; every answer starts on a
  // default so a live rate shows straight away.
  const SECTIONS = [
    { id: 'purpose', keys: ['purpose', 'liveIn', 'exit'] },
    { id: 'property', keys: ['propertyType', 'regionGroup', 'charge'] },
    { id: 'applicant', keys: ['applicantType', 'adverse'] },
  ];
  const touchedKeys = new Set<string>();
  const continuedSections = new Set<string>();
  const pillHooks: Partial<Record<ChoiceKey, () => void>> = {};

  function buildPills(container: HTMLElement, options_: [string, string][], key: ChoiceKey) {
    container.innerHTML = options_.map(([value, label]) =>
      `<button type="button" class="pill" data-value="${value}" aria-pressed="${state[key] === value}">${CHECK_SVG}${label}</button>`).join('');
    container.addEventListener('click', (e) => {
      const btn = (e.target as Element).closest<HTMLButtonElement>('button[data-value]');
      if (!btn) return;
      state[key] = btn.dataset.value as string;
      container.querySelectorAll('button[data-value]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
      touchedKeys.add(key);
      pillHooks[key]?.();
      scheduleFetchResults();
      updateSections();
      advanceFrom(container);
    });
  }

  // After each answer, move to the next visible question; after the last, to the results.
  function advanceFrom(current: Element) {
    const stops = parts.all('#refine [data-advance-stop]');
    const next = stops.slice(stops.indexOf(current as HTMLElement) + 1).find((el) => el.offsetParent !== null);
    if (!next) {
      scrollTo($('results'));
      $('results-headline').focus({ preventScroll: true });
      return;
    }
    scrollTo(next.closest('.q, .field') || next, 'nearest');
    const focusTarget = next.matches('.pills') ? (next.querySelector<HTMLElement>('.pill[aria-pressed="true"]') || next.querySelector<HTMLElement>('.pill')) : next;
    focusTarget?.focus({ preventScroll: true });
  }

  function sectionSummary(id: string): string {
    if (id === 'purpose') {
      return [labelFor(form.purposes, state.purpose), isRefurb() && state.gdv > 0 ? 'GDV ' + money(state.gdv) : '',
        isPurchase() && state.belowValue && state.purchasePrice > 0 ? 'price ' + money(state.purchasePrice) : '',
        state.exit !== 'other_undecided' ? labelFor(form.exits, state.exit).toLowerCase() : '',
        state.liveIn === 'yes' ? "you'll live there · regulated" : 'not living there'].filter(Boolean).join(' · ');
    }
    if (id === 'property') {
      const where = state.regionGroup === 'uk' ? (state.postcode.trim() ? labelFor(form.regions, state.region) : 'UK') : labelFor(form.regionGroups, state.regionGroup);
      return [labelFor(form.propertyTypes, state.propertyType), state.propertySubtype, where, labelFor(form.charges, state.charge).toLowerCase()].filter(Boolean).join(' · ');
    }
    return [labelFor(form.applicantTypes, state.applicantType), state.adverse === 'clean' ? 'clean credit' : labelFor(form.adverse, state.adverse)].join(' · ');
  }

  function updateSections() {
    let currentAssigned = false;
    SECTIONS.forEach((s, i) => {
      const complete = continuedSections.has(s.id) || s.keys.every((k) => touchedKeys.has(k));
      const sectionEl = parts.all(`[data-section="${s.id}"]`)[0];
      sectionEl.classList.toggle('is-complete', complete);
      sectionEl.classList.toggle('is-collapsed', continuedSections.has(s.id));
      parts.all(`[data-badge="${s.id}"]`)[0].innerHTML = complete ? BADGE_CHECK_SVG : String(i + 1);
      parts.all(`[data-summary="${s.id}"]`)[0].textContent = sectionSummary(s.id);
      const progressEl = parts.all(`[data-progress="${s.id}"]`)[0];
      const isCurrent = !complete && !currentAssigned;
      if (isCurrent) currentAssigned = true;
      progressEl.classList.toggle('is-done', complete);
      progressEl.classList.toggle('is-current', isCurrent);
      const btn = progressEl.querySelector('button') as HTMLButtonElement;
      if (isCurrent) btn.setAttribute('aria-current', 'step'); else btn.removeAttribute('aria-current');
      (progressEl.querySelector('.num') as HTMLElement).innerHTML = complete ? BADGE_CHECK_SVG : String(i + 1);
    });
    const compareEl = parts.all('[data-progress="compare"]')[0];
    compareEl.classList.toggle('is-current', !currentAssigned);
    const compareBtn = compareEl.querySelector('button') as HTMLButtonElement;
    if (!currentAssigned) compareBtn.setAttribute('aria-current', 'step'); else compareBtn.removeAttribute('aria-current');
  }

  parts.all<HTMLButtonElement>('[data-continue]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.continue as string;
      continuedSections.add(id);
      updateSections();
      const idx = SECTIONS.findIndex((s) => s.id === id);
      const next = SECTIONS.slice(idx + 1).find((s) => !continuedSections.has(s.id));
      scrollTo(next ? $('sec-' + next.id) : $('results'));
    });
  });
  parts.all<HTMLButtonElement>('[data-change]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.change as string;
      continuedSections.delete(id);
      updateSections();
      const sectionEl = $('sec-' + id);
      scrollTo(sectionEl);
      const firstPill = sectionEl.querySelector<HTMLElement>('.pill[aria-pressed="true"]') || sectionEl.querySelector<HTMLElement>('.pill');
      firstPill?.focus({ preventScroll: true });
    });
  });
  // Buttons that used to be #anchor links.
  parts.all<HTMLButtonElement>('[data-scroll]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.scroll as string;
      const el = target === 'estimate' ? parts.all('.estimate-card')[0] : parts.maybe(target);
      if (el) scrollTo(el);
    });
  });

  // ---------- Refurbishment details (light/heavy refurbishment only) ----------
  // Sent to the adviser with the enquiry; not matched on. Purchase costs are
  // a % of the purchase price with a linked £ figure; contingency is a % of
  // the refurbishment costs.
  function showGdvError(force: boolean) {
    const errEl = $('gdv-error');
    const message = gdvError(isRefurb(), state.gdv, state.value);
    // Newly shown on blur/submit only, cleared as soon as it's valid.
    if (message && (force || !errEl.classList.contains('hidden'))) {
      errEl.textContent = message;
      errEl.classList.remove('hidden');
    } else if (!message) {
      errEl.classList.add('hidden');
    }
    parts.all('.gdv-field')[0].classList.toggle('is-invalid', !errEl.classList.contains('hidden'));
  }
  function refreshRefurbDerived() {
    const purchaseGbpEl = $<HTMLInputElement>('input-purchase-costs-gbp');
    state.purchaseCostsGbp = state.alreadyOwned ? 0 : Math.round(state.value * state.purchaseCostsPct / 100);
    if (parts.activeElement() !== purchaseGbpEl) purchaseGbpEl.value = formatMoneyInput(state.purchaseCostsGbp);
    $('refurb-total').textContent = money(refurbTotalGbp(state));
    $('output-contingency-gbp').textContent = formatMoneyInput(contingencyGbp(state));
    showGdvError(false);
  }
  // The questions that depend on the purpose (docs/bridging-criteria-plan.md Phase 7): refurbishment
  // figures, a similar project before (heavy refurbishment), and a purchase price below value.
  function updateRefurbFields() {
    $('refurb-fields').classList.toggle('hidden', !isRefurb());
    $('similar-project-q').classList.toggle('hidden', state.purpose !== 'refurb_heavy');
    $('below-value-fields').classList.toggle('hidden', !isPurchase());
    showGdvError(false);
  }
  pillHooks.purpose = updateRefurbFields;
  {
    const pctEl = $<HTMLInputElement>('input-purchase-costs-pct');
    const gbpEl = $<HTMLInputElement>('input-purchase-costs-gbp');
    pctEl.addEventListener('input', () => {
      state.purchaseCostsPct = Math.min(100, Math.max(0, parseFloat(pctEl.value) || 0));
      refreshRefurbDerived();
      updateSections();
    });
    gbpEl.addEventListener('input', () => {
      state.purchaseCostsGbp = applyMoneyInputFormatting(gbpEl);
      state.purchaseCostsPct = state.value > 0 ? (state.purchaseCostsGbp / state.value) * 100 : 0;
      pctEl.value = formatPct(state.purchaseCostsPct);
    });
    gbpEl.addEventListener('blur', () => { gbpEl.value = formatMoneyInput(state.purchaseCostsGbp); });
    $<HTMLInputElement>('input-already-owned').addEventListener('change', (e) => {
      state.alreadyOwned = (e.target as HTMLInputElement).checked;
      pctEl.disabled = state.alreadyOwned;
      gbpEl.disabled = state.alreadyOwned;
      $('purchase-costs-pair').classList.toggle('is-disabled', state.alreadyOwned);
      refreshRefurbDerived();
    });
    ([['input-build-costs', 'buildCosts'], ['input-professional-costs', 'professionalCosts'], ['input-gdv', 'gdv']] as [string, NumericStateKey][]).forEach(([id, key]) => {
      const el = $<HTMLInputElement>(id);
      el.addEventListener('input', () => {
        state[key] = applyMoneyInputFormatting(el);
        if (el.value === '0') el.value = '';
        refreshRefurbDerived();
        updateSections();
        // The works and the value after them are matched on (loan to value after works, loan to cost).
        scheduleFetchResults();
      });
    });
    $('input-gdv').addEventListener('blur', () => showGdvError(true));
    $<HTMLInputElement>('input-contingency-pct').addEventListener('input', (e) => {
      state.contingencyPct = Math.min(100, Math.max(0, parseFloat((e.target as HTMLInputElement).value) || 0));
      refreshRefurbDerived();
      scheduleFetchResults();
    });
    // A purchase below value: the price, for lenders' max loan to purchase price.
    const priceField = $('purchase-price-field');
    $<HTMLInputElement>('input-below-value').addEventListener('change', (e) => {
      state.belowValue = (e.target as HTMLInputElement).checked;
      priceField.classList.toggle('hidden', !state.belowValue);
      scheduleFetchResults();
      updateSections();
    });
    const priceEl = $<HTMLInputElement>('input-purchase-price');
    priceEl.addEventListener('input', () => {
      state.purchasePrice = applyMoneyInputFormatting(priceEl);
      if (priceEl.value === '0') priceEl.value = '';
      scheduleFetchResults();
      updateSections();
    });
  }

  buildPills($('input-purpose'), form.purposes, 'purpose');
  updateRefurbFields();
  buildPills($('input-adverse'), form.adverse, 'adverse');
  buildPills($('input-exit'), form.exits, 'exit');
  buildPills($('input-similar-project'), form.similarProject, 'similarProject');
  buildPills($('input-applicant-type'), form.applicantTypes, 'applicantType');

  // Second charge: ask what's outstanding on the existing mortgage(s) — the
  // quote's combined LTV, and sent to the adviser.
  const updateFirstChargeField = () => $('first-charge-field').classList.toggle('hidden', state.charge !== 'second_charge');
  pillHooks.charge = updateFirstChargeField;
  buildPills($('charge-seg'), form.charges, 'charge');
  {
    const el = $<HTMLInputElement>('input-first-charge');
    el.addEventListener('input', () => { state.firstChargeGbp = applyMoneyInputFormatting(el); if (el.value === '0') el.value = ''; scheduleFetchResults(); });
  }
  updateFirstChargeField();

  const subtypeFieldEl = $('property-subtype-field');
  const subtypeSelectEl = $<HTMLSelectElement>('input-property-subtype');
  function updatePropertySubtypeField() {
    const opts = form.propertySubtypesByType[state.propertyType];
    if (!opts) {
      subtypeFieldEl.classList.add('hidden');
      state.propertySubtype = '';
      return;
    }
    if (!opts.includes(state.propertySubtype)) state.propertySubtype = opts[0];
    subtypeSelectEl.innerHTML = opts.map((o) => `<option ${state.propertySubtype === o ? 'selected' : ''}>${o.replace(/&/g, '&amp;')}</option>`).join('');
    subtypeFieldEl.classList.remove('hidden');
  }
  // Clearly development finance: point to the enquiry form. Not for someone living there (regulated), who stays with an adviser.
  function updateDevelopmentNotes() {
    $('refurb-development-note').classList.toggle('hidden', state.liveIn === 'yes');
    $('land-development-note').classList.toggle('hidden', state.liveIn === 'yes' || !['land_with_pp', 'land_without_pp'].includes(state.propertyType));
  }
  pillHooks.propertyType = () => { updatePropertySubtypeField(); updateDevelopmentNotes(); };
  buildPills($('input-property-type'), form.propertyTypes, 'propertyType');
  subtypeSelectEl.addEventListener('change', () => { state.propertySubtype = subtypeSelectEl.value; updateSections(); advanceFrom(subtypeSelectEl); });
  updatePropertySubtypeField();

  const updateLiveInNote = () => $('live-in-regulated-note').classList.toggle('hidden', state.liveIn !== 'yes');
  updateLiveInNote();
  updateDevelopmentNotes();
  $('change-term-link').addEventListener('click', () => {
    const slider = $('input-term-slider');
    scrollTo(slider, 'center');
    slider.focus();
  });
  // Living there makes it regulated, which caps the term.
  pillHooks.liveIn = () => { applyTermCap(); updateLiveInNote(); updateDevelopmentNotes(); render(); };
  buildPills($('liveIn-seg'), form.liveIn, 'liveIn');

  // The visible location question; United Kingdom is narrowed to a region by the postcode.
  function syncRegionFromGroup() {
    $('postcode-field').classList.toggle('hidden', state.regionGroup !== 'uk');
    state.region = state.regionGroup === 'uk' ? classifyUkPostcode(form.postcodeAreaRegions, state.postcode) : state.regionGroup;
  }
  const regionSelect = $<HTMLSelectElement>('input-region-group');
  regionSelect.value = state.regionGroup;
  syncRegionFromGroup();
  regionSelect.addEventListener('change', () => {
    state.regionGroup = regionSelect.value;
    touchedKeys.add('regionGroup');
    syncRegionFromGroup();
    scheduleFetchResults();
    updateSections();
    advanceFrom(regionSelect);
  });
  $<HTMLInputElement>('input-postcode').addEventListener('input', (e) => {
    state.postcode = (e.target as HTMLInputElement).value;
    syncRegionFromGroup();
    scheduleFetchResults();
    updateSections();
  });

  // ---------- Quick estimate: value, loan, term ----------
  // The example figures clear on first click into each field; leaving
  // without typing puts them back (so the live rate never stops showing).
  const moneyFieldsEdited = new Set<string>();
  function wireMoneyField(id: string, key: 'value' | 'loan') {
    const el = $<HTMLInputElement>(id);
    el.value = formatMoneyInput(state[key]);
    el.addEventListener('focus', () => { if (!moneyFieldsEdited.has(id)) el.value = ''; });
    el.addEventListener('blur', () => {
      if (!moneyFieldsEdited.has(id) && el.value === '' && !(id === 'input-loan' && state.sortBy === 'maxLoan')) el.value = formatMoneyInput(state[key]);
    });
    el.addEventListener('input', () => {
      moneyFieldsEdited.add(id);
      state[key] = applyMoneyInputFormatting(el);
      render();
      scheduleFetchResults();
    });
  }
  wireMoneyField('input-value', 'value');
  wireMoneyField('input-loan', 'loan');

  parts.all<HTMLButtonElement>('[data-quick-ltv]').forEach((el) => {
    el.addEventListener('click', () => {
      state.loan = Math.round(state.value * Number(el.dataset.quickLtv) / 100 / 500) * 500;
      moneyFieldsEdited.add('input-loan');
      $<HTMLInputElement>('input-loan').value = formatMoneyInput(state.loan);
      render();
      scheduleFetchResults();
    });
  });

  const termSliderEl = $<HTMLInputElement>('input-term-slider');
  const termNumberEl = $<HTMLInputElement>('input-term-number');
  const termLabel = () => { $('term-label').textContent = state.term + ' months'; };
  function applyTermCap() {
    const capped = state.liveIn === 'yes';
    const max = capped ? form.term.regulatedMax : form.term.max;
    termSliderEl.max = String(max);
    termNumberEl.max = String(max);
    $('term-regulated-note').classList.toggle('hidden', !capped);
    if (Number(state.term) > max) {
      state.term = String(max);
      termSliderEl.value = state.term;
      termNumberEl.value = state.term;
      termLabel();
    }
  }
  termSliderEl.value = state.term;
  termNumberEl.value = state.term;
  termLabel();
  applyTermCap();
  termSliderEl.addEventListener('input', () => {
    state.term = termSliderEl.value;
    termNumberEl.value = termSliderEl.value;
    termLabel();
    scheduleFetchResults();
  });
  termNumberEl.addEventListener('input', () => {
    const max = Number(termSliderEl.max) || form.term.max;
    const clamped = Math.min(max, Math.max(form.term.min, Number(termNumberEl.value) || form.term.min));
    state.term = String(clamped);
    termSliderEl.value = state.term;
    termNumberEl.value = state.term;
    termLabel();
    scheduleFetchResults();
  });

  const ltv = () => (state.value > 0 ? (state.loan / state.value) * 100 : 0);

  function render() {
    const l = ltv();
    $('ltv-label').textContent = l.toFixed(1) + '%';
    $('ltv-note').textContent =
      l > 90 ? 'Very high LTV — likely to sharply reduce the available products.' :
      l > 75 ? 'Above 75% reduces the available products and tends to lift the rate.' :
      l > 0 ? 'Comfortably inside typical bridging LTV ranges.' : '';
    refreshRefurbDerived();
    updateCaseSummary();
  }

  // "Your case": the desktop card and the mobile sticky bar.
  function updateCaseSummary() {
    const maxLoanMode = state.sortBy === 'maxLoan';
    const values: Record<string, string> = {
      value: money(state.value),
      loan: maxLoanMode ? 'Maximum available' : money(state.loan),
      ltv: maxLoanMode ? '—' : ltv().toFixed(1) + '%',
      term: state.term + (state.term === '1' ? ' month' : ' months'),
      'term-short': state.term + ' mo',
    };
    parts.all('[data-case]').forEach((el) => { el.textContent = values[el.dataset.case as string]; });
  }

  // ---------- Results ----------
  let showAllResults = false;

  function renderResultRows() {
    const showGrossLoan = state.sortBy === 'maxLoan';
    $('results-header-grid').innerHTML = resultsHeaderHtml(showGrossLoan);
    $('results-table').classList.toggle('with-gross', showGrossLoan);
    const existingChargesGbp = state.charge === 'second_charge' ? state.firstChargeGbp : 0;
    const sorted = sortResults(state.results, state.sortBy, state.value, existingChargesGbp);
    const visible = showAllResults ? sorted : sorted.slice(0, RESULTS_PREVIEW_COUNT);
    const lowestCost = lowestTotalCostGbp(state.results);
    const rowsEl = $('results-rows');
    rowsEl.innerHTML = visible.map((r, i) => resultRowHtml(r, {
      loanGbp: state.loan, propertyValueGbp: state.value, existingChargesGbp, showGrossLoan, rowIndex: i, brandName: brand.name,
      isBest: !r.isRoa && lowestCost !== null && Math.round(r.totalCostGbp) === lowestCost,
    })).join('');
    $('results-more-wrap').classList.toggle('hidden', sorted.length <= RESULTS_PREVIEW_COUNT);
    $('results-show-all-btn').textContent = showAllResults ? `Show top ${RESULTS_PREVIEW_COUNT} only` : `Show all ${sorted.length} products`;
    rowsEl.querySelectorAll<HTMLButtonElement>('[data-toggle-details]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const wrap = $(btn.getAttribute('aria-controls') as string);
        const open = wrap.classList.toggle('open');
        btn.textContent = open ? 'Details ▴' : 'Details ▾';
        btn.setAttribute('aria-expanded', String(open));
      });
    });
    rowsEl.querySelectorAll<HTMLButtonElement>('[data-apply]').forEach((btn) => {
      btn.addEventListener('click', () => { state.selectedCode = btn.dataset.apply as string; apply.openApplyDialog(); });
    });
  }

  const loanInputEl = $<HTMLInputElement>('input-loan');
  function applySortBy(sortBy: SortBy) {
    state.sortBy = sortBy;
    $<HTMLSelectElement>('input-results-sort').value = sortBy;
    const maxLoanMode = sortBy === 'maxLoan';
    $('ltv-section').classList.toggle('hidden', maxLoanMode);
    $('ltv-badge').classList.toggle('hidden', maxLoanMode);
    $('ltv-note').classList.toggle('hidden', maxLoanMode);
    // Still editable: typing an amount switches back to a normal comparison.
    loanInputEl.value = maxLoanMode ? '' : formatMoneyInput(state.loan);
    loanInputEl.placeholder = maxLoanMode ? 'Maximum loan' : '';
    $('input-loan-prefix').classList.toggle('hidden', maxLoanMode);
    if (state.results.length > 0) renderResultRows();
    $('max-loan-toggle-btn').setAttribute('aria-pressed', String(maxLoanMode));
    render();
  }
  $('results-show-all-btn').addEventListener('click', () => { showAllResults = !showAllResults; renderResultRows(); });
  $('max-loan-toggle-btn').addEventListener('click', () => applySortBy(state.sortBy === 'maxLoan' ? 'totalCost' : 'maxLoan'));
  $<HTMLSelectElement>('input-results-sort').addEventListener('change', (e) => applySortBy((e.target as HTMLSelectElement).value as SortBy));
  loanInputEl.addEventListener('input', () => {
    if (state.sortBy === 'maxLoan' && loanInputEl.value.trim() !== '') applySortBy('totalCost');
  });

  // ---------- "Not ready to talk yet?": email me these results ----------
  // The server works the products out again from this quote's answers
  // (routes/quote.ts), so only the quote id, a first name and an email go.
  // Not in the admin preview.
  const emailResultsForm = $<HTMLFormElement>('email-results-form');
  const emailResultsShownAt = Date.now();
  let emailedQuoteId: string | null = null;
  function resetEmailResults(): void {
    if (emailedQuoteId === null || emailedQuoteId === state.quoteId) return;
    emailedQuoteId = null;
    show('email-results-status', false);
    $<HTMLButtonElement>('email-results-btn').textContent = 'Email me these results';
  }
  emailResultsForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = $<HTMLButtonElement>('email-results-btn');
    const errorEl = $('email-results-error');
    const statusEl = $('email-results-status');
    const firstName = $<HTMLInputElement>('email-results-name').value.trim();
    const email = $<HTMLInputElement>('email-results-email').value.trim();
    show('email-results-error', false);
    if (!firstName || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errorEl.textContent = 'Please enter your first name and a valid email address.';
      show('email-results-error', true);
      return;
    }
    if (!state.quoteId || btn.disabled) return;
    const quoteId = state.quoteId;
    btn.disabled = true;
    btn.textContent = 'Sending…';
    try {
      const res = await fetch(`/api/quote/${encodeURIComponent(quoteId)}/email-results`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ firstName, email, company: $<HTMLInputElement>('email-results-company').value, formRenderedAt: emailResultsShownAt }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.error === 'string' && data.error !== 'quote_not_found' ? data.error : 'Sorry, we couldn’t send that. Please try again, or call us.');
      }
      emailedQuoteId = quoteId;
      statusEl.textContent = `Sent to ${email}. If it isn’t in your inbox in a few minutes, check your spam folder.`;
      show('email-results-status', true);
      btn.textContent = 'Sent ✓';
      track('email_results', { finance_type: form.financeType });
    } catch (err) {
      errorEl.textContent = (err as Error).message;
      show('email-results-error', true);
      btn.textContent = 'Email me these results';
    } finally {
      btn.disabled = false;
    }
  });

  // ---------- "Rather talk it through?": request a callback ----------
  // A first name, phone and email; the server reads the case from this quote
  // (routes/quote.ts, src/quoteCallback.ts) and tells the advisers. Shown under
  // the results, and when nothing matches, since that's when a broker helps most.
  const callbackForm = $<HTMLFormElement>('callback-form');
  const callbackShownAt = Date.now();
  let calledBackQuoteId: string | null = null;
  function resetCallback(): void {
    if (calledBackQuoteId === null || calledBackQuoteId === state.quoteId) return;
    calledBackQuoteId = null;
    show('callback-status', false);
    $<HTMLButtonElement>('callback-btn').textContent = 'Call me back';
  }
  callbackForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = $<HTMLButtonElement>('callback-btn');
    const errorEl = $('callback-error');
    const firstName = $<HTMLInputElement>('callback-name').value.trim();
    const phone = $<HTMLInputElement>('callback-phone').value.trim();
    const email = $<HTMLInputElement>('callback-email').value.trim();
    show('callback-error', false);
    const problem = !firstName ? 'Please enter your first name.'
      : phone.replace(/\D/g, '').length < 10 ? 'Please enter a phone number we can call you on.'
        : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'Please enter a valid email address.' : '';
    if (problem) {
      errorEl.textContent = problem;
      show('callback-error', true);
      return;
    }
    if (!state.quoteId || btn.disabled) return;
    const quoteId = state.quoteId;
    btn.disabled = true;
    btn.textContent = 'Sending…';
    try {
      const res = await fetch(`/api/quote/${encodeURIComponent(quoteId)}/callback`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          firstName, phone, email, bestTime: $<HTMLSelectElement>('callback-time').value, productCode: state.selectedCode,
          company: $<HTMLInputElement>('callback-company').value, formRenderedAt: callbackShownAt,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.error === 'string' && data.error !== 'quote_not_found' ? data.error : `Sorry, we couldn’t send that. Please try again, or call us on ${brand.phone}.`);
      }
      calledBackQuoteId = quoteId;
      $('callback-status').textContent = `Thanks, ${firstName}. A broker will call you on ${phone} to talk these figures through.`;
      show('callback-status', true);
      btn.textContent = 'Sent ✓';
      track('callback_request', { finance_type: form.financeType });
      track('generate_lead', { finance_type: form.financeType, method: 'callback' });
    } catch (err) {
      errorEl.textContent = (err as Error).message;
      show('callback-error', true);
      btn.textContent = 'Call me back';
    } finally {
      btn.disabled = false;
    }
  });

  /** The callback and email cards: callback with any answer (matches or none), email only with matches; neither in the admin preview. */
  function showLeadForms(matches: boolean | null): void {
    const admin = options.mode === 'admin';
    callbackForm.classList.toggle('hidden', admin || matches === null);
    $('callback-title').textContent = matches === false ? 'No match here? A broker may still find one' : 'Rather talk it through?';
    emailResultsForm.classList.toggle('hidden', admin || matches !== true);
  }

  // ---------- Drop-off tracking (funnel.ts) ----------
  if (options.mode !== 'admin') {
    // The first change to the figures or answers (not the lead forms under the results).
    const started = (e: Event) => {
      const target = e.target as Element | null;
      if (!target || target.closest('form.email-results')) return;
      if (e.type === 'click' && !target.closest('button[data-value], [data-quick-ltv], #max-loan-toggle-btn')) return;
      trackOnce('calc_start', { finance_type: form.financeType });
    };
    for (const root of parts.roots) {
      root.addEventListener('input', started);
      root.addEventListener('click', started);
    }
    trackWhenSeen($('results'), 'calc_results_seen', { finance_type: form.financeType });
  }

  let fetchDebounce: ReturnType<typeof setTimeout> | undefined;
  function scheduleFetchResults() {
    render();
    clearTimeout(fetchDebounce);
    fetchDebounce = setTimeout(() => { void fetchResults(); }, 500);
  }

  const show = (id: string, visible: boolean) => $(id).classList.toggle('hidden', !visible);

  // A product chosen on another page ("Apply" on the regulated bridging page): once the figures are in,
  // say whether it's among the results, with its "Get exact terms" here.
  const applyRef = options.applyRef ?? null;
  function updateApplyPick(): void {
    if (!applyRef) return;
    const el = $('apply-pick');
    const row = state.results.find((r) => r.ref === applyRef);
    el.innerHTML = row
      ? `<p>You chose <strong>${escapeHtml(row.lenderName || row.code)}</strong>${row.productName ? ` · ${escapeHtml(row.productName)}` : ''}. Check the figures above are yours, then:</p>
         <button type="button" class="btn btn-primary" data-apply-pick>Get exact terms for this product</button>`
      : '<p>The product you chose isn’t available for these figures or this kind of case. The products below are: change the figures above to look again.</p>';
    show('apply-pick', true);
    el.querySelector('[data-apply-pick]')?.addEventListener('click', () => { state.selectedCode = row!.code; apply.openApplyDialog(); });
  }

  async function fetchResults(): Promise<void> {
    const token = ++state.apiFetchToken;
    $('results-loan-label').textContent = money(state.loan);

    if (!(state.value > 0 && state.loan > 0)) {
      $('results-headline').textContent = 'Complete the details above';
      $('results-criteria-line').textContent = '';
      show('results-wrap', false); show('no-results-wrap', false); show('not-ready-wrap', true); show('results-sort-wrap', false);
      showLeadForms(null);
      $('rate-panel-headline').textContent = 'Enter a property value and loan amount to see available products';
      show('rate-panel-headline', true); show('rate-panel-figure-wrap', false); show('rate-panel-breakdown', false);
      return;
    }
    show('not-ready-wrap', false);
    $('results-headline').textContent = 'Checking available products…';
    $('rate-panel-headline').textContent = 'Checking available products…';
    show('rate-panel-headline', true); show('rate-panel-figure-wrap', false); show('rate-panel-breakdown', false);

    const body = {
      financeType: form.financeType,
      purpose: state.purpose,
      propertyType: state.propertyType,
      propertySubtype: state.propertySubtype || undefined,
      regulatedFlag: state.liveIn === 'yes',
      propertyValueGbp: state.value,
      loanAmountGbp: state.loan,
      chargeType: state.charge,
      // 2nd charge: matched and priced on combined LTV (migration 119).
      ...(state.charge === 'second_charge' ? { existingChargesBalanceGbp: state.firstChargeGbp || 0 } : {}),
      region: state.region,
      // For products lending only in major UK cities (migration 121).
      majorCity: state.regionGroup === 'uk' && form.majorCityPostcodeAreas.includes(postcodeArea(state.postcode)),
      termMonths: Number(state.term),
      creditProfile: state.adverse,
      exitStrategy: state.exit,
      applicantType: state.applicantType,
      // The criteria answers the form asks (docs/bridging-criteria-plan.md Phase 7); each rules nothing out until given.
      ...(isPurchase() && state.belowValue && state.purchasePrice > 0 ? { purchasePriceGbp: state.purchasePrice } : {}),
      ...(isRefurb() && (refurbTotalGbp(state) > 0 || state.gdv > state.value)
        ? { works: { costGbp: refurbTotalGbp(state) + contingencyGbp(state), ...(state.gdv > state.value ? { gdvGbp: state.gdv } : {}) } } : {}),
      ...(state.purpose === 'refurb_heavy' && state.similarProject ? { similarProjectBefore: state.similarProject === 'yes' } : {}),
    };
    const endpoint = options.mode === 'admin' ? '/api/quote/stage1-admin-preview' : '/api/quote/stage1';

    let data: { quoteId: string; results: ResultRow[]; matchedCount: number; errors?: { field: string; message: string }[] };
    try {
      const res = await fetch(endpoint, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
      data = await res.json();
      if (!res.ok) throw new Error((data.errors || []).map((e) => e.field + ': ' + e.message).join('; ') || 'Request failed');
    } catch (err) {
      if (token !== state.apiFetchToken) return;
      $('results-headline').textContent = 'Could not check available products';
      $('results-criteria-line').textContent = (err as Error).message;
      $('rate-panel-headline').textContent = 'Could not check available products';
      return;
    }
    if (token !== state.apiFetchToken) return; // superseded by a newer request

    state.quoteId = data.quoteId;
    state.results = data.results;
    resetEmailResults();
    resetCallback();
    state.matchedCount = data.matchedCount;
    updateApplyPick();

    const adverseLabel = labelFor(form.adverse, state.adverse);
    $('results-criteria-line').textContent = [
      labelFor(form.purposes, state.purpose), labelFor(form.propertyTypes, state.propertyType), labelFor(form.charges, state.charge),
      labelFor(form.regions, state.region), adverseLabel === 'None' ? 'Clean credit' : adverseLabel,
      labelFor(form.applicantTypes, state.applicantType), state.term + ' months',
    ].join(' · ');

    if (data.results.length === 0) {
      $('results-headline').textContent = 'No matches';
      show('results-wrap', false); show('results-sort-wrap', false); show('no-results-wrap', true);
      showLeadForms(false);
      $('rate-panel-headline').textContent = 'No matches for these figures';
      return;
    }

    $('results-headline').textContent = data.results.length + (data.results.length === 1 ? ' product matches your case' : ' products match your case');
    show('no-results-wrap', false); show('results-wrap', true); show('results-sort-wrap', true);
    showLeadForms(true);
    renderResultRows();

    // The estimate card's rate panel: the best (first) match's figures.
    const best = data.results[0];
    show('rate-panel-headline', false); show('rate-panel-figure-wrap', true); show('rate-panel-breakdown', true);
    $('rate-panel-figure').textContent = best.rateMonthlyPct.toFixed(2) + '%';
    $('rate-panel-gross').textContent = money(state.loan + best.feeAmountGbp);
    $('rate-panel-interest').textContent = money(best.totalCostGbp - best.feeAmountGbp);
    $('rate-panel-total').textContent = money(best.totalCostGbp);
  }

  // What the shared application dialog needs to know about a bridging case.
  const adapter: ApplyAdapter = {
    financeType: form.financeType,
    state,
    caseNoun: 'case',
    timescaleNote: 'Completion needed',
    selectedProduct(code) {
      const sel = state.results.find((r) => r.code === code);
      if (!sel) return null;
      const fee = sel.feePct > 0 ? sel.feePct.toFixed(1) + '% arrangement fee' : (sel.feeAmountGbp > 0 ? money(sel.feeAmountGbp) + ' arrangement fee' : 'no arrangement fee');
      return {
        lenderName: sel.lenderName || sel.code,
        summary: `${sel.productName ? sel.productName + ' · ' : ''}${sel.rateMonthlyPct.toFixed(2)}% pm · ${fee} · ${money(sel.totalCostGbp)} over ${state.term} months`,
      };
    },
    showCompanyField: () => !!state.applicantType && state.applicantType !== 'individual',
    // Regulated cases don't get the free-text box.
    showComments: () => state.liveIn !== 'yes',
    extraCommentBlocks: () => [
      state.propertySubtype ? `Property subtype: ${state.propertySubtype}` : '',
      isRefurb() ? refurbCommentBlock({ ...state, propertyValueGbp: state.value }) : '',
      state.charge === 'second_charge' ? firstChargeCommentBlock(state.firstChargeGbp, state.loan, state.value) : '',
    ],
    validationError() {
      const message = gdvError(isRefurb(), state.gdv, state.value);
      if (!message) return '';
      showGdvError(true);
      return 'Please check the GDV in the refurbishment details — ' + message;
    },
    portfolio: {
      isPortfolio: () => state.propertyType.startsWith('mixed_portfolio'),
      propertyValueGbp: () => state.value,
      propertyTypeLabel: () => labelFor(form.propertyTypes, state.propertyType),
    },
    refetch: fetchResults,
  };
  const apply = setupApply(parts, brand, adapter);

  $('advisers-select-btn').addEventListener('click', () => {
    state.selectedCode = null;
    apply.openApplyDialog();
  });

  // Live product count on the estimate card.
  void (async () => {
    try {
      const res = await fetch('/api/quote/panel-stats');
      if (!res.ok) return;
      const stats = await res.json() as { bridgingProductCount: number };
      $('estimate-product-count').textContent = String(stats.bridgingProductCount);
    } catch { /* keep the dash */ }
  })();

  render();
  updateSections();
  void fetchResults();
}
